import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setProgress, setToast } from "../../../redux/slice/system";
import {
  axios,
  AxiosMulti,
  formatApiResult,
} from "../../../axios/request/common";
import { ProjectAPI } from "../../../axios/api";
import {
  useAbortedEffect,
  useHookForm,
  useFilterList,
} from "../../../components/hooks";

import ModuleBreadCrumb from "../../../components/breadCrumb";
import {
  FormInputtext,
  FormDropdown,
  FormCheckbox,
} from "../../../components/formElement/index";
import {
  formatResData,
  checkFormUpdate,
  formatOptions,
} from "../../../service/common";
import { option_diagram } from "../../../service/option/diagram";
import {
  option_enabled,
  option_system_web,
} from "../../../service/option/common";

import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";

export default function BackProjectDetail() {
  const route_params = useParams();
  const route_navigate = useNavigate();
  const dispatch = useDispatch();
  const crumbItem = [
    {
      key: 1,
      label: "專案管理",
      command: () => route_navigate("/back/project"),
    },
    { key: 2, label: "編輯" },
  ];
  const [reload, setReload] = useState({
    list: true,
  });
  const disabled = false;
  // 列表資料
  const [listData, setListData] = useState([]);
  // 明細資料
  const [apiData, setApiData] = useState({});
  // 表單欄位預設值
  const defaultValues = {
    name: "",
    url: [],
    projectID: null,
    status: 1,
  };
  const {
    handleSubmit,
    getValues,
    setValue,
    reset,
    trigger,
    watch,
    yup,
    setSchema,
    errors,
    setting,
    setIsFormUpdate,
  } = useHookForm({
    defaultValues,
  });
  const { getFilterListData } = useFilterList();
  const [chart, setChart] = useState({
    term: "",
    data: [],
  });
  const [optionData, setOptionData] = useState({});
  // 監聽欄位
  const watchColumn = {
    projectID: watch("projectID"),
  };

  useEffect(() => {
    setSchema(
      yup
        .object({
          name: yup.string().required("必填"),
          projectID: yup.string().required("必填").nullable(),
          url: yup
            .array()
            .test("isValidPass", "必填", (value) => value.length > 0),
        })
        .required()
    );
  }, []);

  // 動態判斷欄位
  useEffect(() => {
    let url = [];
    if (apiData.url && apiData.projectID === getValues("projectID"))
      url = apiData.url;
    setValue("url", url);
  }, [watchColumn.projectID]);

  useEffect(() => {
    if (!watchColumn.projectID) return;

    let temp = [...option_diagram];
    temp = temp.filter((item) =>
      item.systemWeb.includes(watchColumn.projectID)
    );

    setChart((state) => ({
      ...state,
      data: temp,
    }));
  }, [watchColumn.projectID]);

  useAbortedEffect(
    (signal) => {
      if (!reload.list) return;

      dispatch(setProgress({ show: true }));

      let isAdd = route_params.detailid === "add",
        promise = [
          ProjectAPI.getProjectList({
            options: { signal },
          }),
        ];
      if (!isAdd)
        promise.push(
          ProjectAPI.getProjectDetail({
            path: `/${route_params.detailid}`,
            options: { signal },
          })
        );

      AxiosMulti(promise).then(
        axios.spread((result_list = {}, result_detail = {}) => {
          let tempOptions = {};

          // 列表資料
          let { data: result_list_data } = result_list;
          if (result_list.Response === 1) {
            setListData(result_list_data);
          }

          // 明細資料
          let { data: result_detail_data } = result_detail;
          if (result_detail.Response === 1) {
            result_detail_data.url = result_detail_data.url.split("*,*");
            let temp = formatResData(defaultValues, result_detail_data);
            setApiData({ ...result_detail_data, ...temp });
            reset(temp);
          }

          let isError = formatApiResult({
            data: [result_list, result_detail],
            action: (message) => {
              dispatch(
                setToast({
                  severity: "error",
                  summary: message,
                  detail: "",
                })
              );
            },
          });
          if (isError) {
            dispatch(setProgress({ show: false }));
            return;
          }

          setOptionData(tempOptions);
          setReload((state) => ({ ...state, list: false }));
          dispatch(setProgress({ show: false }));
        })
      );
    },
    [reload]
  );

  const onSubmit = (submitData) => {
    dispatch(setProgress({ show: true }));
    setIsFormUpdate(false);

    let isAdd = route_params.detailid === "add",
      jsonData = {
        ...submitData,
        url: submitData.url ? submitData.url.join("*,*") : "",
      };

    if (!checkFormUpdate(jsonData, apiData)) {
      dispatch(
        setToast({
          severity: "warn",
          summary: "無資料變更",
          detail: "",
        })
      );
      dispatch(setProgress({ show: false }));
      route_navigate("/back/project");
      return;
    } else if (listData.some((item) => item.name === jsonData.name && isAdd)) {
      dispatch(
        setToast({
          severity: "warn",
          summary: "『專案名稱』重複",
          detail: "",
        })
      );
      dispatch(setProgress({ show: false }));
      return;
    }

    let promise,
      toast = "";
    if (isAdd) {
      // 新增
      promise = ProjectAPI.setProject({
        data: jsonData,
      });
      toast = "新增";
    } else {
      // 修改
      promise = ProjectAPI.updateProject({
        path: `/${apiData.mappingURLID}`,
        data: { ...jsonData },
      });
      toast = "修改";
    }

    promise.then((result) => {
      let { Response, message } = result;
      if (Response === 1) {
        dispatch(
          setToast({
            severity: "success",
            summary: `${toast}成功`,
            detail: "",
          })
        );
        route_navigate("/back/project");
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: `${toast}失敗`,
            detail: message ?? "",
          })
        );
        dispatch(setProgress({ show: false }));
      }
    });
  };

  return (
    <>
      <ModuleBreadCrumb crumbItem={crumbItem} />

      <div className="border-round-lg bg-white mt-3 p-3">
        <h2 className="mt-2 mb-4 ml-2">專案管理 - 編輯</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="formgrid grid">
          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="name"
              className={classNames(
                { "p-error": errors.name },
                "col-12 sm:col-2 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>專案名稱
            </label>
            <div className="col-12 sm:col-10 lg:col-5">
              <FormInputtext
                setting={setting}
                data={{
                  name: "name",
                  disabled: disabled,
                }}
              />
            </div>
          </div>

          {/* <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="status"
              className={classNames(
                { "p-error": errors.status },
                "col-12 sm:col-2 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>啟用狀態
            </label>
            <div className="col-12 sm:col-10 lg:col-5">
              <FormDropdown
                className="w-full"
                setting={setting}
                data={{
                  name: "status",
                  options: option_enabled,
                  optionLabel: "label",
                  optionValue: "value",
                  showClear: false,
                }}
              />
            </div>
          </div> */}

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="projectID"
              className={classNames(
                { "p-error": errors.projectID },
                "col-12 sm:col-2 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>專案來源
            </label>
            <div className="col-12 sm:col-10 lg:col-5">
              <FormDropdown
                className="w-full"
                setting={setting}
                data={{
                  name: "projectID",
                  options: option_system_web,
                  showClear: false,
                }}
              />
            </div>
          </div>

          <div className="flex flex-column align-items-start sm:flex-row field col-12">
            <label
              htmlFor="url"
              className={classNames(
                { "p-error": errors.url },
                "col-12 sm:col-2 text-left sm:text-right mb-1 sm:mb-0 sm:mt-2"
              )}
            >
              圖表
            </label>
            <div className="col-12 sm:col-10">
              <div className="flex align-items-center mb-1">
                <InputText
                  value={chart.term}
                  placeholder="關鍵字查詢"
                  onChange={(e) =>
                    setChart((state) => ({ ...state, term: e.target.value }))
                  }
                />
                <Button
                  type="button"
                  icon="pi pi-refresh"
                  className="p-button-secondary p-button-sm ml-2"
                  onClick={() => setChart((state) => ({ ...state, term: "" }))}
                />
              </div>
              <div className="flex flex-wrap align-items-start border-1 border-400 min-h-5rem p-1">
                {(chart.data || []).map((group, gIdx) => (
                  <React.Fragment key={`chart_group_${group.path}`}>
                    {gIdx !== 0 && <Divider className="my-2" />}

                    <div className="flex flex-wrap align-items-center w-full">
                      <div className="text-lg font-bold w-full ml-1 mb-1">
                        {group.label}
                      </div>
                      {((group.child || []).length === 0 ||
                        (
                          getFilterListData({
                            data: group.child,
                            filterKey: "label",
                            serachTerm: chart.term,
                          }) || []
                        ).length === 0) &&
                        group.child !== null && (
                          <div className="text-red-400 ml-1 mt-1">無資料</div>
                        )}
                      {(
                        getFilterListData({
                          data: group.child,
                          filterKey: "label",
                          serachTerm: chart.term,
                        }) || []
                      ).map((item) => (
                        <div
                          key={`chart_group_${group.id}_${item.path}`}
                          className="inline-flex align-items-center w-full sm:w-6 md:w-4 xl:w-3"
                        >
                          <FormCheckbox
                            className="flex justify-content-start m-0"
                            setting={setting}
                            data={{
                              name: "url",
                              label: item.label,
                              value: item.path,
                              disabled: disabled,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="field col-12 text-right">
            <Button
              type="button"
              label="取消"
              className="p-button-secondary p-button-outlined ml-auto px-3"
              onClick={() => route_navigate("/back/project")}
            />
            {!disabled && (
              <Button type="submit" label="儲存" className="ml-2 px-3" />
            )}
          </div>
        </form>
      </div>
    </>
  );
}
